import dayjs from "dayjs";
import { useState } from "react";
import axios from "axios";
import { apiGet, apiPost } from "../../../utils/apiFetch";
import apiPath from "../../../utils/apiPath";
import useToastContext from "hooks/useToastContext";
import { AiOutlineFilePdf, AiFillEye, AiOutlineReload, AiOutlineDownload } from "react-icons/ai";
import Swal from "sweetalert2";

// 🛠️ FIXED: match Credibled/Certn's real score vocabulary ("Cleared", "Clear",
// "Consider", "Hit", "Fail"...) instead of the literal strings 'PASS' / 'FAIL'
// which are never actually sent by the provider.
const CLEAR_SCORES = ['clear', 'cleared', 'pass', 'passed'];
const FAIL_SCORES = ['fail', 'failed', 'hit', 'hits'];

const BackgroundCheckDetail = ({ check, onBack }) => {
  const notification = useToastContext();
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isRefreshingLink, setIsRefreshingLink] = useState(false);
  const [isFetchingReport, setIsFetchingReport] = useState(false);
  const [currentApplicationUrl, setCurrentApplicationUrl] = useState(check?.applicationUrl);

  if (!check) return null;

  const getStatusColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    switch (status) {
      case 'Complete': return 'bg-green-100 text-green-800';
      case 'In Progress':
      case 'Analyzing': return 'bg-blue-100 text-blue-800';
      case 'Waiting On Candidate':
      case 'Action Required': return 'bg-yellow-100 text-yellow-800';
      case 'Cancelled':
      case 'In Dispute': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // 🛠️ FIXED: was exact-match 'clear' against "Cleared" (never matched). Now
  // substring-matched, case-insensitively, and also recognizes 'Partial' — the
  // value the backend now stores when a multi-check order has a mix of results
  // (e.g. one check cleared, another still pending or came back different).
  const getResultColor = (resultStatus) => {
    if (!resultStatus) return 'bg-yellow-100 text-yellow-500';
    const s = resultStatus.toLowerCase();
    if (FAIL_SCORES.some(f => s.includes(f))) return 'bg-red-100 text-red-800';
    if (CLEAR_SCORES.some(c => s.includes(c))) return 'bg-green-100 text-green-800';
    if (s.includes('partial') || s.includes('unknown') || s.includes('consider')) return 'bg-yellow-100 text-yellow-800';
    return 'bg-yellow-100 text-yellow-500';
  };

  // 🛠️ FIXED: same PASS/FAIL exact-match bug as the list component.
  const getSimpleResultText = (resultData) => {
    if (!resultData) return 'Pending';
    if (typeof resultData === 'string') return resultData;
    if (Array.isArray(resultData) && resultData.length > 0) {
      const scores = resultData
        .map(scan => (scan.score || '').toString().toLowerCase())
        .filter(Boolean);

      if (scores.length === 0) return 'Pending';

      const hasFail = scores.some(s => FAIL_SCORES.some(f => s.includes(f)));
      const allClear = scores.every(s => CLEAR_SCORES.some(c => s.includes(c)));
      const anyClear = scores.some(s => CLEAR_SCORES.some(c => s.includes(c)));

      if (hasFail) return 'Fail';
      if (allClear) return 'Pass';
      if (anyClear) return 'Partial';
    }
    return 'Pending';
  };

  // 🆕 Payment status color helper
  const getPaymentStatusColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    switch (status.toLowerCase()) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleResendEmail = async () => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to resend the application email to the candidate?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3b82f6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Resend it!",
      cancelButtonText: "Cancel"
    });

    if (!result.isConfirmed) return;

    setIsSendingEmail(true);
    try {
      const result = await apiPost(`${apiPath.backgroundCheck}/${check._id}/trigger-email`);
      if (result?.data?.success) {
        Swal.fire({ icon: "success", title: "Email Sent!", timer: 2000, showConfirmButton: false });
        notification.success("Application email resent successfully!");
        if (onBack) onBack();
      } else {
        notification.error(result?.data?.message || "Failed to resend email");
      }
    } catch (error) {
      notification.error("Something went wrong while sending email");
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleRefreshApplicationLink = async () => {
    setIsRefreshingLink(true);
    try {
      const result = await apiGet(`${apiPath.backgroundCheck}/${check._id}/application-link`);
      if (result?.data?.success) {
        const newUrl = result.data.results?.application_url;
        if (newUrl) {
          setCurrentApplicationUrl(newUrl);
          notification.success("Application link refreshed successfully!");
        } else {
          notification.error("Application URL not available yet.");
        }
      } else {
        notification.error(result?.data?.message || "Failed to refresh link");
      }
    } catch (error) {
      notification.error("Something went wrong while refreshing link");
    } finally {
      setIsRefreshingLink(false);
    }
  };

  // 🆕 Fetch/download the report PDF.
  // The endpoint returns raw binary PDF bytes (not JSON), so this uses axios directly
  // with responseType: 'blob' rather than the JSON-oriented apiGet helper.
  const handleFetchReport = async () => {
    setIsFetchingReport(true);
    try {
      const token = localStorage.getItem("token"); // adjust to match however your app stores the auth token
      const baseURL = process.env.REACT_APP_API_BASE_URL || "";

      const response = await axios.get(
        `${baseURL}${apiPath.backgroundCheck}/${check._id}/report-pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `background_check_report_${check.credibledUUID || check._id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      notification.success("Report downloaded successfully!");
    } catch (error) {
      let message = "Failed to fetch the report.";
      try {
        // When responseType is 'blob', error bodies from the server also arrive as blobs
        const text = await error.response?.data?.text?.();
        const parsed = text ? JSON.parse(text) : null;

        if (error.response?.status === 404) {
          message = "Report not available yet — the check hasn't been submitted to the provider.";
        } else if (error.response?.status === 400) {
          message = "Report not ready — the check isn't complete yet.";
        } else if (error.response?.status === 502) {
          message = "Provider is temporarily unreachable. Please try again in a moment.";
        } else if (parsed?.message) {
          message = parsed.message;
        }
      } catch (_) {
        // fall back to default message
      }
      notification.error(message);
    } finally {
      setIsFetchingReport(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Check Details</h2>
        <button onClick={onBack} className="text-gray-600 hover:text-gray-800">← Back to List</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <DetailSection
            title="Candidate Information"
            items={[
              { label: "Name", value: `${check?.employeeData?.firstName || ''} ${check?.employeeData?.lastName || ''}` },
              { label: "Email", value: check?.employeeData?.email || 'N/A' },
              { label: "Country", value: check?.employeeData?.country || 'N/A' }
            ]}
          />

          <DetailSection
            title="Selected Background Checks"
            items={check?.selectedCheckTypes?.map((ct, index) => ({
              label: `Check ${index + 1}`,
              value: `${ct.name}`
            })) || [{ label: "Checks", value: "None selected" }]}
          />

          <DetailSection
            title="Order Information"
            items={[
              { label: "Credibled Application ID", value: check?.applicationId || 'N/A' },
              { label: "Report ID", value: check?.reportId || 'N/A' },
              { label: "Ordered At", value: dayjs(check?.orderedAt).format("MM-DD-YYYY h:mm A") }
            ]}
          />

          {/* 🆕 PAYMENT DETAILS */}
          {check?.payment && (
            <DetailSection
              title="Payment Details"
              items={[
                { label: "Transaction ID", value: check.payment.transactionId || 'N/A' },
                {
                  label: "Amount",
                  value: check.payment.amount
                    ? `$${Number(check.payment.amount).toFixed(2)} ${check.payment.currency || 'USD'}`
                    : 'N/A'
                },
              ]}
            />
          )}
        </div>

        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-4">Status & Results</h3>
            <div className="space-y-4">

              {/* Status Badge */}
              <div className="flex flex-col gap-2">
                <p className="text-sm text-gray-500">Status</p>
                <div className="flex items-center gap-3">
                  <StatusBadge
                    label=""
                    value={check?.status || 'Unknown'}
                    colorClass={getStatusColor(check?.status)}
                  />

                  {check?.status === 'Action Required' && (
                    <a
                      href={check.applicationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-1.5 bg-red-50 border border-red-300 text-red-700 text-xs font-medium rounded-md hover:bg-red-100 transition-colors"
                    >
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                      </span>
                      Resolve Action
                    </a>
                  )}
                </div>
              </div>

              {/* 🆕 PAYMENT STATUS BADGE */}
              {check?.payment?.status && (
                <div>
                  <p className="text-sm text-gray-500">Payment Status</p>
                  <span
                    className={`inline-block px-3 py-1.5 rounded-full text-sm font-semibold ${getPaymentStatusColor(
                      check.payment.status
                    )}`}
                  >
                    {check.payment.status === 'paid' ? 'Paid' : check.payment.status}
                  </span>
                </div>
              )}

              {/* Result */}
              {(check?.resultData || check?.resultStatus) && (
                <div>
                  <p className="text-sm text-gray-500">Result</p>
                  <span className={`inline-block px-3 py-1.5 rounded-full text-sm font-bold ${getResultColor(check?.resultStatus)}`}>
                    {getSimpleResultText(check?.resultData)}
                  </span>
                </div>
              )}

              {/* Email Status */}
              {check.credEmailSent !== undefined && (
                <div className="flex items-center gap-3">
                  <p className="text-sm text-gray-600">
                    Application Email:{" "}
                    {check.credEmailSent ? (
                      <span className="text-green-600 font-medium">Sent</span>
                    ) : (
                      <span className="text-yellow-600 font-medium">Not Sent</span>
                    )}
                  </p>
                  {!check.credEmailSent && (
                    <button
                      onClick={handleResendEmail}
                      disabled={isSendingEmail}
                      className="px-3 py-1 text-xs font-medium text-LightBlue border border-LightBlue rounded hover:bg-LightBlue hover:text-white transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSendingEmail ? "Resending..." : "Resend"}
                    </button>
                  )}
                </div>
              )}

              <div>
                <p className="text-sm text-gray-500">Requested By</p>
                <p>{check?.requestedBy?.name || "Unknown"} ({check?.requestedBy?.email || "N/A"})</p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Application URL</p>
                <div className="flex items-center gap-2">
                  {currentApplicationUrl ? (
                    <a href={currentApplicationUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline break-all text-sm">
                      Open Candidate Application
                    </a>
                  ) : (<span className="text-gray-500">N/A</span>)}

                  <button
                    onClick={handleRefreshApplicationLink}
                    disabled={isRefreshingLink}
                    className="p-1 text-gray-500 hover:text-LightBlue transition-colors disabled:opacity-50"
                    title="Refresh Application Link"
                  >
                    <AiOutlineReload className={`w-4 h-4 ${isRefreshingLink ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* 🆕 Report section — replaced the old pdfUrl-only block with an on-demand fetch */}
          <div className="pt-4 border-t">
            <h3 className="text-sm font-medium text-gray-500 mb-3">Report</h3>
            <div className="space-y-2">
              {check?.status === 'Complete' ? (
                <button
                  onClick={handleFetchReport}
                  disabled={isFetchingReport}
                  className="flex items-center justify-center bg-red-50 hover:bg-red-100 text-red-700 px-4 py-2.5 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full"
                >
                  <div className="w-5 h-5 mr-2">
                    {isFetchingReport ? <AiOutlineReload className="animate-spin" /> : <AiOutlineDownload />}
                  </div>
                  {isFetchingReport ? "Fetching Report..." : "Fetch Report PDF"}
                </button>
              ) : (
                <div className="text-sm text-gray-500 italic">
                  Report will be available once the background check is completed.
                </div>
              )}

              {/* Kept for backward compatibility with any legacy records that still have reportUrl */}
              {check?.reportUrl && (
                <ReportLink href={check.reportUrl} icon={<AiFillEye />} label="View Online Report" color="blue" />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DetailSection = ({ title, items }) => (
  <div>
    <h3 className="text-sm font-medium text-gray-500">{title}</h3>
    <div className="mt-2 space-y-2">
      {items.map((item, index) => (
        <div key={index} className={item.value && item.value.length > 50 ? "whitespace-pre-wrap break-words" : ""}>
          {item.label && <strong>{item.label}: </strong>}
          <span>{item.value || "N/A"}</span>
        </div>
      ))}
    </div>
  </div>
);

const StatusBadge = ({ label, value, colorClass }) => (
  <div>
    <p className="text-sm text-gray-500">{label}</p>
    <span className={`inline-block px-3 py-1.5 rounded-full text-sm ${colorClass}`}>
      {value}
    </span>
  </div>
);

const ReportLink = ({ href, icon, label, color }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className={`flex items-center justify-center bg-${color}-50 hover:bg-${color}-100 text-${color}-700 px-4 py-2.5 rounded transition-colors`}>
    <div className="w-5 h-5 mr-2">{icon}</div>
    {label}
  </a>
);

export default BackgroundCheckDetail;